/**
 * Accounts List Hook
 * Manages fetching and displaying branch accounts
 */

import { useState, useEffect } from 'react';
import { accountsAPI } from '@/src/lib/api/accounts';
import type { BranchAccount, AccountFilters } from '@/src/types/account';

interface UseAccountsProps {
  branchId?: number;
  filters?: AccountFilters;
  autoLoad?: boolean;
}

export function useAccounts({ branchId, filters, autoLoad = true }: UseAccountsProps = {}) {
  const [accounts, setAccounts] = useState<BranchAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Load accounts from API
   */
  const loadAccounts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      let accountsData: BranchAccount[];
      
      if (branchId) {
        accountsData = await accountsAPI.getByBranch(branchId, filters);
      } else {
        accountsData = await accountsAPI.getAll(filters);
      }
      
      setAccounts(accountsData);
    } catch (err) {
      console.error('Failed to load accounts:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete an account
   */
  const deleteAccount = async (accountId: number) => {
    try {
      setIsLoading(true);
      await accountsAPI.delete(accountId);
      await loadAccounts(); // Reload list
    } catch (err) {
      console.error('Failed to delete account:', err);
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refresh accounts list
   */
  const refresh = () => {
    loadAccounts();
  };

  // Auto-load on mount if enabled
  useEffect(() => {
    if (autoLoad) {
      loadAccounts();
    }
  }, [branchId, autoLoad]);

  return {
    accounts,
    isLoading,
    error,
    loadAccounts,
    deleteAccount,
    refresh,
  };
}
